export const isEmpty = <T extends string | number | symbol, K>(obj: Record<T, K>): boolean => Object.keys(obj).length === 0;

declare const __DEV__: boolean;

export const pick = <T extends string | number | symbol, K>(obj: Record<T, K>, props: Array<T> | T): Record<T, K> => {
  if (__DEV__ && !Array.isArray(props) && !(typeof props === "string" || typeof props === "number" || typeof props === "symbol"))
    throw new Error("Props must be array or string | number | symbol");

  const propsArray = Array.isArray(props) ? props : [props];
  const existing = propsArray.filter((p) => obj[p] !== undefined);
  return existing.reduce((previousValue, currentValue) => {
    return { ...previousValue, [currentValue]: obj[currentValue] };
  }, {} as Record<T, K>);
};

const omitOne = <T extends string | number | symbol, K>(obj: Record<T, K>, prop: T): Omit<Record<T, K>, T> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [prop]: omitted, ...res } = obj;
  return res;
};

export const omit = <T extends string | number | symbol, K>(obj: Record<T, K>, prop: Array<T> | T): Omit<Record<T, K>, T> => {
  if (Array.isArray(prop)) {
    return prop.reduce((a, b) => {
      return omitOne(a, b) as Record<T, K>;
    }, obj);
  }
  return omitOne(obj, prop);
};

export const isObject = (obj: unknown): boolean => {
  return !Array.isArray(obj) && ((typeof obj === "object" && obj !== null) || typeof obj === "function");
};

/** A machine-readable format for specifying errors in HTTP API responses based on rfc7807. */
export type ProblemDetail = {
  /** A URI reference [RFC3986] that identifies the problem type. */
  type: string | null;
  /** A short, human-readable summary of the problem type.  */
  title?: string | null;
  /** The HTTP status code([RFC7231], Section 6) generated by the origin server for this occurrence of the problem */
  status: number | null;
  /** A human-readable explanation specific to this occurrence of the problem. */
  detail?: string | null;
  /** A URI reference that identifies the specific occurrence of the problem. */
  instance?: string | null;
};

export type ValidationProblemDetails = {
  type: "https://tools.ietf.org/html/rfc7231#section-6.5.1" | "https://httpstatuses.io/422";
  /** Gets the validation errors associated with this instance */
  errors: { [key: string]: string[] };
} & ProblemDetail;

export const isProblemDetail = (problemDetail: unknown): problemDetail is ProblemDetail => {
  return problemDetail != null && typeof problemDetail === "object" && "type" in problemDetail && "status" in problemDetail;
};

export const isValidationProblemDetail = (problemDetail: unknown): problemDetail is ValidationProblemDetails => {
  return (
    isProblemDetail(problemDetail) &&
    "errors" in problemDetail &&
    (problemDetail.type === "https://tools.ietf.org/html/rfc7231#section-6.5.1" || problemDetail.type === "https://httpstatuses.io/422")
  );
};
